import React, { useContext, useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes, faArrowUp, faArrowDown, faCode, faPaste, faTable, faFileImport, faDatabase } from '@fortawesome/free-solid-svg-icons';
import './styles/CustomizedVariable.css';
import apiService from '../services/apiService';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import VariableContext from '../contexts/VariableContext';

const CustomizedVariable = ({ isOpen, onClose, setcurrentSelectedVariablesset, projectId }) => {
  const { dataGroup, setdataGroup, selectedProject, selectedClient, powner } = useContext(VariableContext);
  const { toast } = useToast();
  const { confirm } = useConfirm();

  // ── Single-variable state ──────────────────────────────────────────────────
  const [rows, setRows] = useState([{ stub: '', logic: '' }]);
  const [variableName, setVariableName] = useState('');
  const [loopVarName, setLoopVarName] = useState('');
  const [availableVariables, setAvailableVariables] = useState([]);
  const [showVariableSelector, setShowVariableSelector] = useState(false);
  const [currentRowIndex, setCurrentRowIndex] = useState(0);
  const [operationType, setOperationType] = useState('OR');
  const [selectedVariables, setSelectedVariables] = useState([]);
  const [advancedMode, setAdvancedMode] = useState(false);
  const [loopVar, setLoopVar] = useState(false);
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pasteData, setPasteData] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [isSavingDG, setIsSavingDG] = useState(false);
  const fileInputRef = useRef(null);

  // ── Bulk-import state ──────────────────────────────────────────────────────
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkRows, setBulkRows] = useState([{ varName: '', condition: '', description: '' }]);
  const [showBulkPasteModal, setShowBulkPasteModal] = useState(false);
  const [bulkPasteData, setBulkPasteData] = useState('');
  const bulkFileInputRef = useRef(null);

  useEffect(() => {
    if (dataGroup) {
      setAvailableVariables(Object.keys(dataGroup).filter(v => !v.startsWith('_')));
    }
  }, [dataGroup]);

  if (!isOpen) return null;

  // ── Single-variable helpers ────────────────────────────────────────────────
  const handleAddRow = () => setRows([...rows, { stub: '', logic: '' }]);

  const handleRemoveRow = (index) => {
    if (rows.length > 1) {
      const newRows = [...rows];
      newRows.splice(index, 1);
      setRows(newRows);
    }
  };

  const handleMoveUp = (index) => {
    if (index > 0) {
      const newRows = [...rows];
      [newRows[index], newRows[index - 1]] = [newRows[index - 1], newRows[index]];
      setRows(newRows);
    }
  };

  const handleMoveDown = (index) => {
    if (index < rows.length - 1) {
      const newRows = [...rows];
      [newRows[index], newRows[index + 1]] = [newRows[index + 1], newRows[index]];
      setRows(newRows);
    }
  };

  const handleInputChange = (index, field, value) => {
    const newRows = [...rows];
    newRows[index][field] = value;
    setRows(newRows);
  };

  const openVariableSelector = (index) => {
    setCurrentRowIndex(index);
    setShowVariableSelector(true);
    setSelectedVariables([]);
  };

  const toggleVariableSelection = (variable) => {
    setSelectedVariables(prev =>
      prev.includes(variable) ? prev.filter(v => v !== variable) : [...prev, variable]
    );
  };

  const applyVariableOperation = () => {
    if (selectedVariables.length === 0) return;
    let logicExpression = '';
    if (advancedMode) {
      logicExpression = rows[currentRowIndex].logic;
    } else {
      const operator = operationType === 'OR' ? ' or ' : operationType === 'AND' ? ' and ' : ' and not ';
      logicExpression = selectedVariables
        .map((v, i) => {
          if (operationType === 'NOT' && i === selectedVariables.length - 1) return `not ${v}`;
          return v;
        })
        .join(operator);
    }
    const newRows = [...rows];
    newRows[currentRowIndex].logic = logicExpression;
    setRows(newRows);
    setShowVariableSelector(false);
  };

  // Parse for single-variable paste (2 cols)
  const parsePastedData = (text) => {
    const lines = text.trim().split(/\r?\n/);
    const parsedRows = [];
    for (const line of lines) {
      let columns = line.split(/\t/);
      if (columns.length === 1) columns = line.split(/,/);
      const stub = columns[0] ? columns[0].trim() : '';
      const logic = columns[1] ? columns[1].trim() : '';
      if (stub || logic) parsedRows.push({ stub, logic });
    }
    return parsedRows;
  };

  const handlePasteFromExcel = async () => {
    if (!pasteData.trim()) { toast.warning('Please paste some data first'); return; }
    const newRows = parsePastedData(pasteData);
    if (newRows.length === 0) { toast.warning('No valid data found. Please ensure your data has at least one column.'); return; }
    const replaceExisting = await confirm({
      title: "Apply Paste",
      message: `Found ${newRows.length} row(s) to paste. Replace existing rows or append?`,
      confirmText: "Replace",
      cancelText: "Append",
    });
    if (replaceExisting) setRows(newRows);
    else setRows([...rows, ...newRows]);
    setShowPasteModal(false);
    setPasteData('');
    toast.success(`Successfully added ${newRows.length} row(s)!`);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      setPasteData(content);
      const previewRows = parsePastedData(content);
      if (previewRows.length > 0) toast.info(`Loaded ${previewRows.length} row(s) from file. Click "Apply Paste" to confirm.`);
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleSave = async () => {
    setSaveError(null);
    if (!variableName || variableName.trim() === '') { toast.warning('Please enter a variable name'); return; }
    if (dataGroup && dataGroup[variableName]) { toast.warning(`Variable "${variableName}" already exists. Please use a different name.`); return; }
    const emptyRows = rows.filter(row => !row.stub.trim() || !row.logic.trim());
    if (emptyRows.length > 0) {
      const ok = await confirm({
        title: "Empty Rows",
        message: `${emptyRows.length} row(s) have empty Group Label or Logic Definition. Do you want to continue anyway?`,
        confirmText: "Continue",
        variant: "warning",
      });
      if (!ok) return;
    }
    setIsSaving(true);
    try {
      let maxLoc = 0;
      if (dataGroup) {
        Object.values(dataGroup).forEach(q => {
          (q.varName || []).forEach(v => {
            if (v.Location && v.Location.includes(":")) {
              const [, end] = v.Location.split(":").map(Number);
              if (end > maxLoc) maxLoc = end;
            }
          });
        });
      }
      const customVariable = {
        name: variableName.trim(),
        type: 'SingleResponse',
        logic: rows.map(row => ({ stub: row.stub.trim(), condition: row.logic.trim() }))
      };
      setcurrentSelectedVariablesset(prev => [...prev, customVariable.name]);
      const valueLabels = {};
      const values = rows.map((row, index) => {
        const value = (index + 1).toString() + ".0";
        valueLabels[value] = row.stub;
        return { index: index + 1, title: row.stub };
      });
      let response = null;
      try {
        const { data } = await apiService.post('/compute-newvar', {
          projectId, customVariable, dataGroup,
          project: JSON.stringify(selectedProject),
          client: JSON.stringify(selectedClient),
          user: powner,
        }, { timeout: 300000 }); // 5-min timeout — SPSS write + OneDrive upload can be slow
        response = { data };
        if (!data || !data.success) throw new Error(data?.message || 'Failed to create custom variable on server');
      } catch (err) {
        throw new Error(err.message || 'An unexpected error occurred');
      }
      if (response && response.data && response.data.success) {
        const updatedDG = {
          ...dataGroup,
          [variableName]: {
            "Question number": variableName,
            Qtitle: variableName,
            instructions: "",
            type: "SingleResponse",
            values,
            valueLabels,
            varName: [{ Name: variableName, Location: `${maxLoc + 1}:${maxLoc + 2}` }],
            "order": Object.keys(dataGroup).length + 1,
            "_order": Object.keys(dataGroup).length + 1
          }
        };
        setdataGroup(updatedDG);

        // Persist the updated variable structure (qgroup_json) back to the database
        try {
          await apiService.post('/save_all_data_list', { projectId, dataGroup: updatedDG });
        } catch (dgErr) {
          console.warn('[saveDataGroup] Structure save failed (non-fatal):', dgErr.message);
        }

        toast.success(`Custom variable "${variableName}" created successfully!`);
        // Modal stays open so the user can review and click "Save Structure" manually.
      } else {
        throw new Error(response?.data?.message || 'Failed to create custom variable');
      }
    } catch (error) {
      console.error('Error creating custom variable:', error);
      setSaveError(error.message);
      toast.error(error.message || 'Error creating custom variable');
      setcurrentSelectedVariablesset(prev => prev.filter(v => v !== variableName));
    } finally {
      setIsSaving(false);
    }
  };

  const testBackendConnection = async () => {
    try { await apiService.get('/health', { timeout: 5000 }); return true; }
    catch { return false; }
  };

  const retrySave = async () => { if (saveError) { setSaveError(null); await handleSave(); } };

  // Save the current in-memory dataGroup structure back to the database (qgroup_json).
  // Called automatically after variable creation, and also available as a manual button.
  const handleSaveDataGroup = async () => {
    setIsSavingDG(true);
    try {
      const { data } = await apiService.post('/save_all_data_list', { projectId, dataGroup });
      if (data?.message) {
        toast.success('Variable structure saved to database');
      }
    } catch (error) {
      toast.error('Failed to save structure: ' + error.message);
    } finally {
      setIsSavingDG(false);
    }
  };

  // ── Bulk-import helpers ────────────────────────────────────────────────────
  const parseBulkPastedData = (text) => {
    const lines = text.trim().split(/\r?\n/);
    const parsedRows = [];
    for (const line of lines) {
      let columns = line.split(/\t/);
      if (columns.length === 1) columns = line.split(/,/);
      const varName    = columns[0] ? columns[0].trim() : '';
      const condition  = columns[1] ? columns[1].trim() : '';
      const description = columns[2] ? columns[2].trim() : '';
      if (varName || condition) parsedRows.push({ varName, condition, description });
    }
    return parsedRows;
  };

  const handleApplyBulkPaste = () => {
    if (!bulkPasteData.trim()) { toast.warning('Please paste some data first'); return; }
    const newRows = parseBulkPastedData(bulkPasteData);
    if (newRows.length === 0) { toast.warning('No valid data found.'); return; }
    setBulkRows(newRows);
    setShowBulkPasteModal(false);
    setBulkPasteData('');
    toast.success(`Parsed ${newRows.length} variable(s) — review and save when ready.`);
  };

  const handleBulkFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      setBulkPasteData(content);
      const preview = parseBulkPastedData(content);
      if (preview.length > 0) toast.info(`Loaded ${preview.length} variable(s) from file.`);
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleBulkRowChange = (index, field, value) => {
    const updated = [...bulkRows];
    updated[index] = { ...updated[index], [field]: value };
    setBulkRows(updated);
  };

  const handleBulkAddRow = () =>
    setBulkRows([...bulkRows, { varName: '', condition: '', description: '' }]);

  const handleBulkRemoveRow = (index) => {
    if (bulkRows.length > 1) {
      const updated = [...bulkRows];
      updated.splice(index, 1);
      setBulkRows(updated);
    }
  };

  const handleBulkSave = async () => {
    // Rows that have both a name AND a condition are "ready to send"
    const readyRows = bulkRows.filter(r => r.varName.trim() && r.condition.trim());
    if (readyRows.length === 0) {
      toast.warning('Please add at least one variable with both a name and a condition');
      return;
    }

    setIsSaving(true);
    try {
      const { data } = await apiService.post('/compute-newvar-bulk', {
        projectId,
        bulkVariables: readyRows.map(r => ({
          varName:     r.varName.trim(),
          condition:   r.condition.trim(),
          description: r.description.trim(),
        })),
        dataGroup,
        project: JSON.stringify(selectedProject),
        client:  JSON.stringify(selectedClient),
        user:    powner,
      }, { timeout: 300000 }); // 5-min timeout — N variables × SPSS write + OneDrive + PG sync

      if (!data || !data.success) {
        throw new Error(data?.error || 'Failed to import variables');
      }

      const createdVars = data.created || [];

      // Build local dataGroup entries for all created variables
      let maxLoc = 0;
      if (dataGroup) {
        Object.values(dataGroup).forEach(q => {
          (q.varName || []).forEach(v => {
            if (v.Location && v.Location.includes(':')) {
              const [, end] = v.Location.split(':').map(Number);
              if (end > maxLoc) maxLoc = end;
            }
          });
        });
      }

      const newEntries = {};
      const newVarNames = [];

      createdVars.forEach((v, idx) => {
        newVarNames.push(v.varName);
        const loc      = maxLoc + idx + 1;
        const isNumeric = v.type !== 'boolean';
        newEntries[v.varName] = {
          'Question number': v.varName,
          Qtitle:       v.description || v.varName,
          instructions: '',
          type:         isNumeric ? 'Numeric' : 'SingleResponse',
          values:       isNumeric ? [] : [{ index: 0, title: 'No' }, { index: 1, title: 'Yes' }],
          valueLabels:  isNumeric ? {} : { '0.0': 'No', '1.0': 'Yes' },
          varName:      [{ Name: v.varName, Location: `${loc}:${loc + 1}` }],
          order:        Object.keys(dataGroup).length + idx + 1,
          _order:       Object.keys(dataGroup).length + idx + 1,
        };
      });

      // Optimistic local state update
      const updatedDG = { ...dataGroup, ...newEntries };
      setcurrentSelectedVariablesset(prev => [...prev, ...newVarNames]);
      setdataGroup(updatedDG);

      // Persist the updated variable structure (qgroup_json) back to the database
      try {
        await apiService.post('/save_all_data_list', { projectId, dataGroup: updatedDG });
      } catch (dgErr) {
        console.warn('[saveDataGroup] Structure save failed (non-fatal):', dgErr.message);
      }

      const errorCount = (data.errors || []).length;
      if (errorCount > 0) {
        toast.warning(
          `Imported ${createdVars.length} variable(s). ${errorCount} failed — see console for details.`
        );
        console.warn('[Bulk Import] partial errors:', data.errors);
      } else {
        toast.success(`Successfully imported ${createdVars.length} variable(s)!`);
      }
      // Modal stays open so the user can review and click "Save Structure" manually.
    } catch (error) {
      console.error('[Bulk Import] Error:', error);
      toast.error(error.message || 'Error importing variables');
    } finally {
      setIsSaving(false);
    }
  };

  const bulkValidCount = bulkRows.filter(r => r.varName.trim()).length;
  const bulkReadyCount = bulkRows.filter(r => r.varName.trim() && r.condition.trim()).length;
  const bulkPreviewRows = parseBulkPastedData(bulkPasteData);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="modal-header">
          <h3>{bulkMode ? 'Bulk Import Variables' : 'Create Custom Variable'}</h3>
          <button onClick={onClose} className="modal-close-button">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* ── Mode Toggle ── */}
        <div className="mode-toggle-bar">
          <button
            className={`mode-tab ${!bulkMode ? 'active' : ''}`}
            onClick={() => setBulkMode(false)}
          >
            Single Variable
          </button>
          <button
            className={`mode-tab ${bulkMode ? 'active' : ''}`}
            onClick={() => setBulkMode(true)}
          >
            <FontAwesomeIcon icon={faTable} /> Bulk Import
          </button>
        </div>

        {/* ════════════════════════════════════════════════════════════════════
            SINGLE VARIABLE MODE (existing)
        ════════════════════════════════════════════════════════════════════ */}
        {!bulkMode && (
          <>
            <div className="variable-name-input">
              <label>Variable Name:</label>
              <input
                type="text"
                value={variableName}
                onChange={(e) => setVariableName(e.target.value)}
                placeholder="Enter variable name"
              />
            </div>

            <label className="toggle-item">
              <input type="checkbox" checked={loopVar} onChange={(e) => setLoopVar(e.target.checked)} />
              <span className="toggle-slider"></span>
              <span className="toggle-text">Bilendi Setup</span>
            </label>

            {loopVar && (
              <div className="variable-name-input">
                <label>Loop by Name:</label>
                <input
                  type="text"
                  value={loopVarName}
                  onChange={(e) => setLoopVarName(e.target.value)}
                  placeholder="Enter variable name"
                />
              </div>
            )}

            <div className="table-container">
              <div className="table-toolbar">
                <button onClick={() => setShowPasteModal(true)} className="paste-excel-btn" title="Paste from Excel">
                  <FontAwesomeIcon icon={faPaste} /> Paste from Excel
                </button>
              </div>
              <table className="custom-var-table">
                <thead>
                  <tr>
                    <th>Group Label</th>
                    <th>Logic Definition</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          type="text"
                          value={row.stub}
                          onChange={(e) => handleInputChange(index, 'stub', e.target.value)}
                          placeholder="Group label"
                        />
                      </td>
                      <td>
                        <div className="logic-input-container">
                          <input
                            type="text"
                            value={row.logic}
                            onChange={(e) => handleInputChange(index, 'logic', e.target.value)}
                            placeholder="e.g., Q1 or Q2"
                          />
                          <button className="variable-selector-btn" onClick={() => openVariableSelector(index)} title="Select variables">
                            <FontAwesomeIcon icon={faCode} />
                          </button>
                        </div>
                      </td>
                      <td className="actions-cell">
                        <button onClick={() => handleMoveUp(index)} disabled={index === 0} title="Move up">
                          <FontAwesomeIcon icon={faArrowUp} />
                        </button>
                        <button onClick={() => handleMoveDown(index)} disabled={index === rows.length - 1} title="Move down">
                          <FontAwesomeIcon icon={faArrowDown} />
                        </button>
                        <button onClick={() => handleRemoveRow(index)} disabled={rows.length <= 1} title="Remove row" className="remove-btn">
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="modal-footer">
              <div className="footer-buttons">
                <button onClick={handleAddRow} className="add-row-btn" disabled={isSaving}>
                  <FontAwesomeIcon icon={faPlus} /> Add Row
                </button>
                <button onClick={handleSave} className={`save-btn ${isSaving ? 'saving' : ''}`} disabled={isSaving}>
                  {isSaving ? (<><span className="spinner"></span> Saving...</>) : 'Save Custom Variable'}
                </button>
                <button
                  onClick={handleSaveDataGroup}
                  className={`save-dg-btn ${isSavingDG ? 'saving' : ''}`}
                  disabled={isSavingDG || isSaving}
                  title="Save the current variable structure (dataGroup JSON) to the database"
                >
                  {isSavingDG
                    ? <><span className="spinner"></span> Saving…</>
                    : <><FontAwesomeIcon icon={faDatabase} /> Save Structure</>}
                </button>
              </div>
              {saveError && (
                <div className="error-message">
                  <span className="error-text">{saveError}</span>
                  <button onClick={retrySave} className="retry-btn">Retry</button>
                </div>
              )}
            </div>
          </>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            BULK IMPORT MODE (new)
        ════════════════════════════════════════════════════════════════════ */}
        {bulkMode && (
          <>
            {/* Instructions banner */}
            <div className="bulk-instructions-banner">
              <div className="bulk-col-legend">
                <div className="bulk-col-pill col1">Col 1 — Variable Name <span className="req-star">*</span></div>
                <div className="bulk-sep">→</div>
                <div className="bulk-col-pill col2">Col 2 — Condition <span className="req-star">*</span></div>
                <div className="bulk-sep">→</div>
                <div className="bulk-col-pill col3">Col 3 — Description <span className="opt-tag">optional</span></div>
              </div>
              <p className="bulk-hint">Each row in Excel becomes a separate new variable.</p>
            </div>

            {/* Toolbar */}
            <div className="bulk-toolbar">
              <button onClick={() => setShowBulkPasteModal(true)} className="paste-excel-btn">
                <FontAwesomeIcon icon={faPaste} /> Paste from Excel
              </button>
              <span className="bulk-count-badge">
                {bulkReadyCount} ready &nbsp;·&nbsp; {bulkValidCount} named &nbsp;·&nbsp; {bulkRows.length} total
              </span>
            </div>

            {/* Editable bulk table */}
            <div className="bulk-table-wrapper">
              <table className="bulk-var-table">
                <thead>
                  <tr>
                    <th className="col-num">#</th>
                    <th className="col-varname">Variable Name <span className="req-star">*</span></th>
                    <th className="col-condition">Condition <span className="req-star">*</span></th>
                    <th className="col-description">Description <span className="opt-tag">optional</span></th>
                    <th className="col-actions">Del</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkRows.map((row, index) => (
                    <tr key={index} className={!row.varName.trim() ? 'bulk-row-empty' : ''}>
                      <td className="col-num">{index + 1}</td>
                      <td>
                        <input
                          type="text"
                          value={row.varName}
                          onChange={(e) => handleBulkRowChange(index, 'varName', e.target.value)}
                          placeholder="e.g. AgeGroup"
                          className={!row.varName.trim() ? 'input-warn' : ''}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={row.condition}
                          onChange={(e) => handleBulkRowChange(index, 'condition', e.target.value)}
                          placeholder="e.g. Q1=1 or Q2=2"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={row.description}
                          onChange={(e) => handleBulkRowChange(index, 'description', e.target.value)}
                          placeholder="Optional description…"
                          className="desc-input"
                        />
                      </td>
                      <td className="actions-cell">
                        <button
                          onClick={() => handleBulkRemoveRow(index)}
                          disabled={bulkRows.length <= 1}
                          title="Remove row"
                          className="remove-btn"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Bulk footer */}
            <div className="modal-footer">
              <div className="footer-buttons">
                <button onClick={handleBulkAddRow} className="add-row-btn">
                  <FontAwesomeIcon icon={faPlus} /> Add Row
                </button>
                <button
                  onClick={handleBulkSave}
                  className={`save-btn bulk-save-btn ${isSaving ? 'saving' : ''}`}
                  disabled={bulkReadyCount === 0 || isSaving}
                  title={bulkReadyCount === 0 ? 'Each variable needs a name and a condition' : ''}
                >
                  {isSaving ? (
                    <><span className="spinner"></span> Importing…</>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faFileImport} />&nbsp;
                      Import {bulkReadyCount > 0 ? `${bulkReadyCount} ` : ''}Variable{bulkReadyCount !== 1 ? 's' : ''}
                    </>
                  )}
                </button>
                <button
                  onClick={handleSaveDataGroup}
                  className={`save-dg-btn ${isSavingDG ? 'saving' : ''}`}
                  disabled={isSavingDG || isSaving}
                  title="Save the current variable structure (dataGroup JSON) to the database"
                >
                  {isSavingDG
                    ? <><span className="spinner"></span> Saving…</>
                    : <><FontAwesomeIcon icon={faDatabase} /> Save Structure</>}
                </button>
              </div>
            </div>
          </>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            BULK PASTE MODAL
        ════════════════════════════════════════════════════════════════════ */}
        {showBulkPasteModal && (
          <div className="variable-selector-modal">
            <div className="variable-selector-content paste-modal">
              <h3>Paste Variables from Excel</h3>

              <div className="paste-instructions">
                <p><strong>Expected column order:</strong></p>
                <div className="bulk-col-legend" style={{ margin: '8px 0' }}>
                  <div className="bulk-col-pill col1">Col 1 — Variable Name</div>
                  <div className="bulk-sep">→</div>
                  <div className="bulk-col-pill col2">Col 2 — Condition</div>
                  <div className="bulk-sep">→</div>
                  <div className="bulk-col-pill col3">Col 3 — Description (optional)</div>
                </div>
                <ul>
                  <li>Select and copy the cells from Excel, then paste below</li>
                  <li>Each row = one new variable</li>
                  <li>Tab-separated (Excel default) or comma-separated are both supported</li>
                </ul>
              </div>

              <div className="paste-textarea-container">
                <textarea
                  value={bulkPasteData}
                  onChange={(e) => setBulkPasteData(e.target.value)}
                  placeholder={
                    'Paste Excel data here…\n\nExample:\n' +
                    'AgeGroup\t18-35 respondents\tYounger segment\n' +
                    'OlderGroup\t36+ respondents\n' +
                    'HighIncome\tIncome=1 or Income=2'
                  }
                  rows={9}
                />
              </div>

              <div className="file-upload-section">
                <input
                  type="file"
                  ref={bulkFileInputRef}
                  onChange={handleBulkFileUpload}
                  accept=".csv,.tsv,.txt"
                  style={{ display: 'none' }}
                />
                <button type="button" onClick={() => bulkFileInputRef.current.click()} className="upload-file-btn">
                  Or Upload CSV / TSV File
                </button>
              </div>

              {/* Live preview */}
              {bulkPasteData.trim() && (
                <div className="paste-preview">
                  <h4>Preview ({bulkPreviewRows.length} variable{bulkPreviewRows.length !== 1 ? 's' : ''} detected):</h4>
                  <div className="preview-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Variable Name</th>
                          <th>Condition</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkPreviewRows.slice(0, 6).map((row, idx) => (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td className={!row.varName ? 'cell-empty' : ''}>{row.varName || '—'}</td>
                            <td className={!row.condition ? 'cell-empty' : ''}>{row.condition || '—'}</td>
                            <td className="cell-desc">{row.description || <em style={{ color: '#aaa' }}>—</em>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {bulkPreviewRows.length > 6 && (
                      <p className="preview-more">+ {bulkPreviewRows.length - 6} more row(s)</p>
                    )}
                  </div>
                </div>
              )}

              <div className="variable-selector-buttons">
                <button onClick={() => { setShowBulkPasteModal(false); setBulkPasteData(''); }} className="cancel-btn">
                  Cancel
                </button>
                <button onClick={handleApplyBulkPaste} className="apply-btn" disabled={!bulkPasteData.trim()}>
                  Apply — Load {bulkPreviewRows.length > 0 ? bulkPreviewRows.length : ''} Variable{bulkPreviewRows.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            SINGLE-VAR PASTE MODAL (existing, unchanged)
        ════════════════════════════════════════════════════════════════════ */}
        {showPasteModal && (
          <div className="variable-selector-modal">
            <div className="variable-selector-content paste-modal">
              <h3>Paste from Excel</h3>

              <div className="paste-instructions">
                <p><strong>Instructions:</strong></p>
                <ul>
                  <li>Copy cells from Excel (2 columns: Group Label and Logic Definition)</li>
                  <li>Paste the data in the text area below</li>
                  <li>Click "Apply Paste" to add the rows</li>
                  <li>You can also upload a CSV or TSV file</li>
                </ul>
                <p><small>Tip: Excel data is copied as tab-separated values</small></p>
              </div>

              <div className="paste-textarea-container">
                <textarea
                  value={pasteData}
                  onChange={(e) => setPasteData(e.target.value)}
                  placeholder="Paste your Excel data here...&#10;&#10;Example:&#10;Group 1&#9;Q1 or Q2&#10;Group 2&#9;Q3 and Q4&#10;Group 3&#9;not Q5"
                  rows={8}
                />
              </div>

              <div className="file-upload-section">
                <label className="file-upload-label">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept=".csv,.tsv,.txt,.xlsx"
                    style={{ display: 'none' }}
                  />
                  <button type="button" onClick={() => fileInputRef.current.click()} className="upload-file-btn">
                    Or Upload File
                  </button>
                </label>
              </div>

              {pasteData && (
                <div className="paste-preview">
                  <h4>Preview (first 5 rows):</h4>
                  <div className="preview-scroll">
                    <table>
                      <thead>
                        <tr><th>Group Label</th><th>Logic Definition</th></tr>
                      </thead>
                      <tbody>
                        {parsePastedData(pasteData).slice(0, 5).map((row, idx) => (
                          <tr key={idx}>
                            <td>{row.stub || '(empty)'}</td>
                            <td>{row.logic || '(empty)'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {parsePastedData(pasteData).length > 5 && (
                      <p className="preview-more">... and {parsePastedData(pasteData).length - 5} more row(s)</p>
                    )}
                  </div>
                </div>
              )}

              <div className="variable-selector-buttons">
                <button onClick={() => { setShowPasteModal(false); setPasteData(''); }} className="cancel-btn">Cancel</button>
                <button onClick={handlePasteFromExcel} className="apply-btn">Apply Paste</button>
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            VARIABLE SELECTOR MODAL (existing, unchanged)
        ════════════════════════════════════════════════════════════════════ */}
        {showVariableSelector && (
          <div className="variable-selector-modal">
            <div className="variable-selector-content">
              <h3>Combine Variables</h3>

              <div className="operation-selector">
                <label>
                  <input type="radio" checked={!advancedMode} onChange={() => setAdvancedMode(false)} />
                  Simple Mode
                </label>
                <label>
                  <input type="radio" checked={advancedMode} onChange={() => setAdvancedMode(true)} />
                  Advanced Mode
                </label>
              </div>

              {!advancedMode ? (
                <>
                  <div className="operation-type">
                    <select value={operationType} onChange={(e) => setOperationType(e.target.value)}>
                      <option value="OR">OR (Any of the selected)</option>
                      <option value="AND">AND (All of the selected)</option>
                      <option value="NOT">AND NOT (All except last)</option>
                    </select>
                  </div>

                  <div className="variable-list">
                    {availableVariables.map(variable => (
                      <div
                        key={variable}
                        className={`variable-item ${selectedVariables.includes(variable) ? 'selected' : ''}`}
                        onClick={() => toggleVariableSelection(variable)}
                      >
                        {variable}
                      </div>
                    ))}
                  </div>

                  <div className="preview-section">
                    <h4>Preview:</h4>
                    <div className="preview-logic">
                      {selectedVariables.length > 0 ? (
                        selectedVariables.map((v, i) => (
                          <React.Fragment key={v}>
                            {i > 0 && (
                              <span className="operator">
                                {operationType === 'OR' ? 'OR' : operationType === 'AND' ? 'AND' : i === selectedVariables.length - 1 ? 'AND NOT' : 'AND'}
                              </span>
                            )}
                            <span className="variable">{v}</span>
                          </React.Fragment>
                        ))
                      ) : (
                        <span className="no-variables">No variables selected</span>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="advanced-mode">
                  <h4>Advanced Logic Builder</h4>
                  <textarea
                    value={rows[currentRowIndex].logic}
                    onChange={(e) => handleInputChange(currentRowIndex, 'logic', e.target.value)}
                    placeholder="Enter custom logic (e.g., (Q1 or Q2) and not Q3)"
                    rows={5}
                  />
                  <div className="available-vars-hint">
                    <p>Available variables: {availableVariables.join(', ')}</p>
                  </div>
                </div>
              )}

              <div className="variable-selector-buttons">
                <button onClick={() => setShowVariableSelector(false)} className="cancel-btn">Cancel</button>
                <button onClick={applyVariableOperation} className="apply-btn">Apply</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default CustomizedVariable;
